"use client"

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { QueryClient, useQuery } from '@tanstack/react-query'
import type { User } from '@supabase/supabase-js'

import { getClient } from '../utils/supabase'
import type Authentication from '../types/authentication'

function pickNonEmptyString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined
}

/** Google (and similar) expose `picture` / `avatar_url` on user_metadata or on the Google identity. */
export function oauthAvatarUrlFromUser(user: User | undefined): string | undefined {
  if (!user) return undefined
  const meta = user.user_metadata as Record<string, unknown> | undefined
  const fromMeta = pickNonEmptyString(meta?.avatar_url) ?? pickNonEmptyString(meta?.picture)
  if (fromMeta) return fromMeta

  const identities = user.identities
  if (!Array.isArray(identities)) return undefined
  for (const identity of identities) {
    if (identity.provider !== 'google') continue
    const data = identity.identity_data as Record<string, unknown> | undefined
    const fromIdentity =
      pickNonEmptyString(data?.avatar_url) ?? pickNonEmptyString(data?.picture)
    if (fromIdentity) return fromIdentity
  }
  return undefined
}

export async function getAuthentication(): Promise<Authentication> {
  const { data: { session } } = await getClient().auth.getSession()
  const userId = session?.user?.id

  let isEditor = false
  if (userId) {
    const { data } = await getClient()
      .from('accounts')
      .select('roles(type)')
      .eq('user_id', userId)
      .maybeSingle()
    isEditor = Array.isArray(data?.roles) && data.roles.some((r: { type: string }) => r.type === 'editor')
  }

  const meta = session?.user?.user_metadata as Record<string, unknown> | undefined
  const pathRaw = meta?.profile_avatar_object_path
  const profileAvatarObjectPath =
    typeof pathRaw === 'string' && pathRaw.trim().length > 0 ? pathRaw.trim() : undefined

  const oauthAvatarUrl = oauthAvatarUrlFromUser(session?.user)

  return {
    email: session?.user?.email,
    id: userId,
    isAnonymous: session?.user?.is_anonymous,
    isAuthenticated: !!userId,
    isEditor,
    profileAvatarObjectPath,
    oauthAvatarUrl,
  } as Authentication
}

export interface UseGetAuthenticationParams {
  redirectIfNotAuthenticated?: boolean
  redirectTo?: string
  requireEditor?: boolean
  redirectIfNotEditor?: string
}

export default function useGetAuthentication(
  {
    redirectIfNotAuthenticated = true,
    redirectTo = "/auth?sign=signin",
    requireEditor = false,
    redirectIfNotEditor = "/",
  }: UseGetAuthenticationParams = {},
  queryClient?: QueryClient,
) {
  const router = useRouter()
  const result = useQuery({
    queryFn: getAuthentication,
    queryKey: ['authentication']
  }, queryClient)
  const { data: authentication, isSuccess, isError, isLoading } = result

  useEffect(() => {
    if (isLoading) return
    if (isError || (!authentication?.isAuthenticated && redirectIfNotAuthenticated && redirectTo)) {
      router.replace(redirectTo)
      return
    }
    if (requireEditor && authentication?.isAuthenticated && !authentication?.isEditor) {
      router.replace(redirectIfNotEditor)
    }
  }, [isLoading, isError, isSuccess, authentication, redirectIfNotAuthenticated, redirectTo, requireEditor, redirectIfNotEditor, router])

  return result
}